import dynamic from 'next/dynamic'

import type {
  SectionsAboutUsInfoComponent,
  SectionsAppAdvertisingComponent,
  SectionsAppComponent,
  SectionsBlocksComponent,
  SectionsBlogListComponent,
  SectionsBoardProfilesComponent,
  SectionsBrandsComponent,
  SectionsCarouselComponent,
  SectionsCashbackComponent,
  SectionsContactChannelsComponent,
  SectionsContactComponent,
  SectionsContactInfoComponent,
  SectionsDeferralCalculatorComponent,
  SectionsDividerComponent,
  SectionsEshopsComponent,
  SectionsExplanationTextComponent,
  SectionsFaqComponent,
  SectionsFeatureComponent,
  SectionsFileListComponent,
  SectionsFocusComponent,
  SectionsFooterComponent,
  SectionsGalleryComponent,
  SectionsHeroComponent,
  SectionsHtmlEmbedComponent,
  SectionsImageBoxSliderComponent,
  SectionsInformationBoxComponent,
  SectionsInstallStepsComponent,
  SectionsInstallmentsCalculatorComponent,
  SectionsInvoiceBannerComponent,
  SectionsListComponent,
  SectionsListHorizontalComponent,
  SectionsMediaArticleContactComponent,
  SectionsMediaArticleListComponent,
  SectionsMoreComponent,
  SectionsOurVisionComponent,
  SectionsPricingPlanComponent,
  SectionsQuotesCarouselComponent,
  SectionsRightImageComponent,
  SectionsSimpleTextComponent,
  SectionsTwistoAccountComponent,
  SectionsTypeformComponent,
  SectionsVideoComponent,
  SectionsVideoStoriesComponent,
} from '@twisto/api-cms'

import {
  Essential,
  type EssentialType,
  PageSpecific,
  type PageSpecificType,
  Specialized,
  type SpecializedType,
} from './section-types'
import {
  AboutUsInfo,
  AppAdvertising,
  AppSection,
  Blocks,
  BlogList,
  BoardProfileInfo,
  Brands,
  Carousel,
  Cashback,
  Contact,
  ContactChannels,
  ContactInfo,
  Divider,
  Eshops,
  ExplanationText,
  Faq,
  Feature,
  FileList,
  Focus,
  Footer,
  Gallery,
  HTMLEmbed,
  Hero,
  ImageBoxSlider,
  InformationBox,
  InstallSteps,
  InvoiceBanner,
  List,
  ListHorizontal,
  MediaArticleContact,
  MediaArticlesList,
  More,
  OurVision,
  PricingPlan,
  QuotesCarousel,
  RightImage,
  SimpleText,
  TwistoAccount,
  Typeform,
  Video,
  VideoStories,
} from './sections'

const InstallmentsCalculator = dynamic(
  () => import('./sections/installments-calculator'),
  {
    ssr: false,
  }
)
const DeferralCalculator = dynamic(
  () => import('./sections/deferral-calculator'),
  {
    ssr: false,
  }
)

export const COMPONENTS_TYPE = {
  SECTION_HERO: 'sections.hero',
  SECTION_PAPER: 'sections.paper',
  SECTION_INSTALL_STEPS: 'sections.install-steps',
  SECTION_DEV: 'sections.dev-section',
  SECTION_FEATURE: 'sections.feature',
  SECTION_ESHOPS: 'sections.eshops',
  SECTION_APP: 'sections.app',
  SECTION_APP_ADVERTISING: 'sections.app-advertising',
  SECTION_PRICING_PLAN: 'sections.pricing-plan',
  SECTION_MORE: 'sections.more',
  SECTION_FAQ: 'sections.faq',
  SECTION_FOOTER: 'sections.footer',
  SECTION_FOCUS: 'sections.focus',
  SECTION_BLOCKS: 'sections.blocks',
  SECTION_CONTACT: 'sections.contact',
  SECTION_LIST: 'sections.list',
  SECTION_BRANDS: 'sections.brands',
  SECTION_IMAGE_BOX_SLIDER: 'sections.image-box-slider',
  SECTION_INFORMATION_BOX: 'sections.information-box',
  SECTION_GALLERY: 'sections.gallery',
  SECTION_VIDEO: 'sections.video',
  SECTION_SIMPLE_TEXT: 'sections.simple-text',
  SECTION_CAROUSEL: 'sections.carousel',
  SECTION_LIST_HORIZONTAL: 'sections.list-horizontal',
  SECTION_INSTALLMENTS_CALCULATOR: 'sections.installments-calculator',
  SECTION_BOARD_PROFILES: 'sections.board-profiles',
  SECTION_ABOUT_US_INFO: 'sections.about-us-info',
  SECTION_TWISTO_ACCOUNT: 'sections.twisto-account',
  SECTION_BLOG_LIST: 'sections.blog-list',
  SECTION_FILE_LIST: 'sections.file-list',
  SECTION_VIDEO_STORIES: 'sections.video-stories',
  SECTION_OUR_VISION: 'sections.our-vision',
  SECTION_DEFERRAL_CALCULATOR: 'sections.deferral-calculator',
  SECTION_DIVIDER: 'sections.divider',
  SECTION_CONTACT_CHANNELS: 'sections.contact-channels',
  SECTION_RIGHT_IMAGE: 'sections.right-image',
  SECTION_QUOTES_CAROUSEL: 'sections.quotes-carousel',
  SECTION_TYPEFORM: 'sections.typeform',
  SECTION_INVOICE_BANNER: 'sections.invoice-banner',
  SECTION_CONTACT_INFO: 'sections.contact-info',
  SECTIONS_CASHBACK: 'sections.cashback',
  SECTION_MEDIA_ARTICLES_LIST: 'sections.media-article-list',
  SECTION_MEDIA_ARTICLE_CONTACT: 'sections.media-article-contact',
  SECTIONS_HTML_EMBED: 'sections.html-embed',
  SECTION_EXPLANATION_TEXT: 'sections.explanation-text',
} as const

export type SectionType =
  | (SectionsHeroComponent & {
      __component: (typeof COMPONENTS_TYPE)['SECTION_HERO']
    })
  | (SectionsInstallStepsComponent & {
      __component: (typeof COMPONENTS_TYPE)['SECTION_INSTALL_STEPS']
    })
  | (SectionsFeatureComponent & {
      __component: (typeof COMPONENTS_TYPE)['SECTION_FEATURE']
    })
  | (SectionsEshopsComponent & {
      __component: (typeof COMPONENTS_TYPE)['SECTION_ESHOPS']
    })
  | (SectionsAppComponent & {
      __component: (typeof COMPONENTS_TYPE)['SECTION_APP']
    })
  | (SectionsAppAdvertisingComponent & {
      __component: (typeof COMPONENTS_TYPE)['SECTION_APP_ADVERTISING']
    })
  | (SectionsPricingPlanComponent & {
      __component: (typeof COMPONENTS_TYPE)['SECTION_PRICING_PLAN']
    })
  | (SectionsMoreComponent & {
      __component: (typeof COMPONENTS_TYPE)['SECTION_MORE']
    })
  | (SectionsFaqComponent & {
      __component: (typeof COMPONENTS_TYPE)['SECTION_FAQ']
    })
  | (SectionsFooterComponent & {
      __component: (typeof COMPONENTS_TYPE)['SECTION_FOOTER']
    })
  | (SectionsContactComponent & {
      __component: (typeof COMPONENTS_TYPE)['SECTION_CONTACT']
    })
  | (SectionsGalleryComponent & {
      __component: (typeof COMPONENTS_TYPE)['SECTION_GALLERY']
    })
  | (SectionsFocusComponent & {
      __component: (typeof COMPONENTS_TYPE)['SECTION_FOCUS']
    })
  | (SectionsBlocksComponent & {
      __component: (typeof COMPONENTS_TYPE)['SECTION_BLOCKS']
    })
  | (SectionsListComponent & {
      __component: (typeof COMPONENTS_TYPE)['SECTION_LIST']
    })
  | (SectionsBrandsComponent & {
      __component: (typeof COMPONENTS_TYPE)['SECTION_BRANDS']
    })
  | (SectionsImageBoxSliderComponent & {
      __component: (typeof COMPONENTS_TYPE)['SECTION_IMAGE_BOX_SLIDER']
    })
  | (SectionsDeferralCalculatorComponent & {
      __component: (typeof COMPONENTS_TYPE)['SECTION_DEFERRAL_CALCULATOR']
    })
  | (SectionsCarouselComponent & {
      __component: (typeof COMPONENTS_TYPE)['SECTION_CAROUSEL']
    })
  | (SectionsInformationBoxComponent & {
      __component: (typeof COMPONENTS_TYPE)['SECTION_INFORMATION_BOX']
    })
  | (SectionsVideoComponent & {
      __component: (typeof COMPONENTS_TYPE)['SECTION_VIDEO']
    })
  | (SectionsSimpleTextComponent & {
      __component: (typeof COMPONENTS_TYPE)['SECTION_SIMPLE_TEXT']
    })
  | (SectionsListHorizontalComponent & {
      __component: (typeof COMPONENTS_TYPE)['SECTION_LIST_HORIZONTAL']
    })
  | (SectionsInstallmentsCalculatorComponent & {
      __component: (typeof COMPONENTS_TYPE)['SECTION_INSTALLMENTS_CALCULATOR']
    })
  | (SectionsBoardProfilesComponent & {
      __component: (typeof COMPONENTS_TYPE)['SECTION_BOARD_PROFILES']
    })
  | (SectionsAboutUsInfoComponent & {
      __component: (typeof COMPONENTS_TYPE)['SECTION_ABOUT_US_INFO']
    })
  | (SectionsTwistoAccountComponent & {
      __component: (typeof COMPONENTS_TYPE)['SECTION_TWISTO_ACCOUNT']
    })
  | (SectionsBlogListComponent & {
      __component: (typeof COMPONENTS_TYPE)['SECTION_BLOG_LIST']
    })
  | (SectionsFileListComponent & {
      __component: (typeof COMPONENTS_TYPE)['SECTION_FILE_LIST']
    })
  | (SectionsVideoStoriesComponent & {
      __component: (typeof COMPONENTS_TYPE)['SECTION_VIDEO_STORIES']
    })
  | (SectionsOurVisionComponent & {
      __component: (typeof COMPONENTS_TYPE)['SECTION_OUR_VISION']
    })
  | (SectionsDividerComponent & {
      __component: (typeof COMPONENTS_TYPE)['SECTION_DIVIDER']
    })
  | (SectionsMediaArticleListComponent & {
      __component: (typeof COMPONENTS_TYPE)['SECTION_MEDIA_ARTICLES_LIST']
    })
  | (SectionsMediaArticleContactComponent & {
      __component: (typeof COMPONENTS_TYPE)['SECTION_MEDIA_ARTICLE_CONTACT']
    })
  | (SectionsContactChannelsComponent & {
      __component: (typeof COMPONENTS_TYPE)['SECTION_CONTACT_CHANNELS']
    })
  | (SectionsRightImageComponent & {
      __component: (typeof COMPONENTS_TYPE)['SECTION_RIGHT_IMAGE']
    })
  | (SectionsQuotesCarouselComponent & {
      __component: (typeof COMPONENTS_TYPE)['SECTION_QUOTES_CAROUSEL']
    })
  | (SectionsTypeformComponent & {
      __component: (typeof COMPONENTS_TYPE)['SECTION_TYPEFORM']
    })
  | (SectionsInvoiceBannerComponent & {
      __component: (typeof COMPONENTS_TYPE)['SECTION_INVOICE_BANNER']
    })
  | (SectionsContactInfoComponent & {
      __component: (typeof COMPONENTS_TYPE)['SECTION_CONTACT_INFO']
    })
  | (SectionsCashbackComponent & {
      __component: (typeof COMPONENTS_TYPE)['SECTIONS_CASHBACK']
    })
  | (SectionsHtmlEmbedComponent & {
      __component: (typeof COMPONENTS_TYPE)['SECTIONS_HTML_EMBED']
    })
  | (SectionsExplanationTextComponent & {
      __component: (typeof COMPONENTS_TYPE)['SECTION_EXPLANATION_TEXT']
    })

export type ComponentType = EssentialType | SpecializedType | PageSpecificType

type SectionsProps = {
  sections?: SectionType[]
  components?: ComponentType[]
}

const Section = ({ sectionData }: { sectionData: SectionType }) => {
  switch (sectionData.__component) {
    case 'sections.hero':
      return <Hero {...sectionData} />
    case 'sections.install-steps':
      return <InstallSteps {...sectionData} />
    case 'sections.feature':
      return <Feature {...sectionData} />
    case 'sections.eshops':
      return <Eshops {...sectionData} />
    case 'sections.app':
      return <AppSection {...sectionData} />
    case 'sections.app-advertising':
      return <AppAdvertising {...sectionData} />
    case 'sections.pricing-plan':
      return <PricingPlan {...sectionData} />
    case 'sections.more':
      return <More {...sectionData} />
    case 'sections.faq':
      return <Faq {...sectionData} />
    case 'sections.footer':
      return <Footer {...sectionData} />
    case 'sections.contact':
      return <Contact {...sectionData} />
    case 'sections.gallery':
      return <Gallery {...sectionData} />
    case 'sections.focus':
      return <Focus {...sectionData} />
    case 'sections.blocks':
      return <Blocks {...sectionData} />
    case 'sections.carousel':
      return <Carousel {...sectionData} />
    case 'sections.list':
      return <List {...sectionData} />
    case 'sections.brands':
      return <Brands {...sectionData} />
    case 'sections.image-box-slider':
      return <ImageBoxSlider {...sectionData} />
    case 'sections.deferral-calculator':
      return <DeferralCalculator {...sectionData} />
    case 'sections.information-box':
      return <InformationBox {...sectionData} />
    case 'sections.video':
      return <Video {...sectionData} />
    case 'sections.simple-text':
      return <SimpleText {...sectionData} />
    case 'sections.list-horizontal':
      return <ListHorizontal {...sectionData} />
    case 'sections.installments-calculator':
      return <InstallmentsCalculator {...sectionData} />
    case 'sections.board-profiles':
      return <BoardProfileInfo {...sectionData} />
    case 'sections.about-us-info':
      return <AboutUsInfo {...sectionData} />
    case 'sections.twisto-account':
      return <TwistoAccount {...sectionData} />
    case 'sections.blog-list':
      return <BlogList {...sectionData} />
    case 'sections.file-list':
      return <FileList {...sectionData} />
    case 'sections.video-stories':
      return <VideoStories {...sectionData} />
    case 'sections.our-vision':
      return <OurVision {...sectionData} />
    case 'sections.divider':
      return <Divider {...sectionData} />
    case 'sections.contact-channels':
      return <ContactChannels {...sectionData} />
    case 'sections.right-image':
      return <RightImage {...sectionData} />
    case 'sections.quotes-carousel':
      return <QuotesCarousel {...sectionData} />
    case 'sections.typeform':
      return <Typeform {...sectionData} />
    case 'sections.invoice-banner':
      return <InvoiceBanner {...sectionData} />
    case 'sections.contact-info':
      return <ContactInfo {...sectionData} />
    case 'sections.cashback':
      return <Cashback {...sectionData} />
    case 'sections.media-article-list':
      return <MediaArticlesList {...sectionData} />
    case 'sections.media-article-contact':
      return <MediaArticleContact {...sectionData} />
    case 'sections.html-embed':
      return <HTMLEmbed {...sectionData} />
    case 'sections.explanation-text':
      return <ExplanationText {...sectionData} />
    default:
      return null
  }
}

const Component = ({ componentData }: { componentData: ComponentType }) => {
  const prefix = componentData.__component.split('.')[0]

  switch (prefix) {
    case 'essentials':
      return <Essential essentialData={componentData as EssentialType} />
    case 'specialized':
      return <Specialized specializedData={componentData as SpecializedType} />
    case 'page-specific':
      return (
        <PageSpecific
          pageSpecificData={componentData as unknown as PageSpecificType}
        />
      )
    default:
      return null
  }
}

// TODO: Remove this once we migrate production layout to new components structure
export const PageSections = ({ sections, components }: SectionsProps) => (
  <>
    {sections?.map((section, index) => (
      <Section key={`${section.__component}${index}`} sectionData={section} />
    ))}

    {components?.map((component, index) => (
      <Component
        key={`${component.__component}${index}`}
        componentData={component}
      />
    ))}
  </>
)
